import clsx from 'clsx';
import { ParlayTicketModel, TicketModel, useTicketBet } from '@/hooks/query';
import { useBetslipStore } from '@/store/betslip';
import { addCommasWithFix } from '@/utils/odds-utils';
import { useRef } from 'react';
import { bettingInputMaxLength } from '@/hooks/query';
import { Tooltip } from '../bet-msg';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { betslipStyle } from '@/features/betslip/betslip.style';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
interface BetInputProps {
	isParlay: boolean;
	canBet: boolean;
	inputCurrency?: string;
	ticketKey: string;
	ticketMinBet: number;
	ticketBetCount?: number;
	type: 'small' | 'normal';
}

export const BetInput = ({
	isParlay,
	canBet,
	inputCurrency,
	type = 'small',
	ticketKey,
	ticketMinBet,
	ticketBetCount
}: BetInputProps) => {
	const { oddsType, isIndependentMode } = useBetslipContext();
	const { refetch, isFetching } = useTicketBet();
	const [
		focusOddsKey,
		setFocusOddsKey,
		selectAllOddsKey,
		setSelectAllOddsKey,
		getTicketByKey,
		updateTicket,
		setMultiStakePerBet,
		setParlayStakePerBet,
		checkStake,
		isBookingBet,
	] = useBetslipStore(state => [
		state.focusOddsKey,
		state.setFocusOddsKey,
		state.selectAllOddsKey,
		state.setSelectAllOddsKey,
		state.getTicketByKey,
		state.updateTicket,
		state.setMultiStakePerBet,
		state.setParlayStakePerBet,
		state.checkStake,
		state.isBookingBet,
	]);
	const { SiteConfigsData } = useGetSiteConfigs();
	const input = useRef<HTMLInputElement>(null);
	let ticket: TicketModel | ParlayTicketModel;
	let ticketStake: number, ticketMaxBet, isMixParlay = false;
	if (isParlay) {
		ticket = getTicketByKey(ticketKey, isParlay) as ParlayTicketModel;
		ticketStake = ticket.stake;
		ticketMaxBet = ticket.MaxBet;
		ticketMinBet = ticketMinBet;
		isMixParlay = ticket.isMixParlay;
	} else {
		ticket = getTicketByKey(ticketKey, isParlay) as TicketModel;
		ticketStake = ticket.stake;
		ticketMaxBet = ticket.maxBet;
		ticketMinBet = ticket.minBet;
	}

	const onFocus = () => {
		if (!canBet) return;

		const key = ticketKey ?? '';

		setFocusOddsKey(key);
		if (ticketStake) {
			setSelectAllOddsKey(key);
		} else {
			setSelectAllOddsKey('');
		}

		input.current?.select();
	};

	const resetStakePerBet = (ticket: TicketModel | ParlayTicketModel) => {
		if (isParlay) {
			if ((ticket as ParlayTicketModel).isComboParlay) {
				setParlayStakePerBet(0);
			}
		} else {
			setMultiStakePerBet(0);
		}
	}

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const input = e.target.value;
		const stake = parseInt(input.toString().replace(/[^0-9]/g, '')) || 0;
		ticket.stake = stake;

		setSelectAllOddsKey('');
		resetStakePerBet(ticket);
		updateTicket(ticket, isParlay, oddsType);
	}

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const code = e.code;
		const isAlphaPressed = code.startsWith('Key');

		if (isAlphaPressed && !e.ctrlKey) {
			e.preventDefault();
			return;
		}

		if (code === 'Enter' || code === 'NumpadEnter') {
			if (checkStake(isIndependentMode, oddsType) && !isFetching) {
				refetch();
			}
			return;
		}

		const stakeLength = (ticketStake ?? 0).toString().length;

		if (stakeLength >= bettingInputMaxLength) {
			ticket.stake = 0;
			updateTicket(ticket, isParlay, oddsType);
		}
	}
	const formatBetValue = (value: number) => {
		if (value >= 1000) {
			return `${(value / 1000).toString()}K`;
		}
		return value.toString();
	};
	
	let placeHolder = ticketMinBet && ticketMaxBet 
		? `${formatBetValue(ticketMinBet)}-${formatBetValue(ticketMaxBet)}` 
		: '';

	/** html 結構問題, 當顯示stake時, div上的place holder會破版 */
	if (ticketStake || !canBet) {
		placeHolder = '';
	}

	return (
		<div
			className={clsx(
				'stake-input',
				type === 'small' ? 'stake-input--small stake-input--placeholder':'',
				betslipStyle.betInput,
				{ [betslipStyle.bookedBetInput]: isBookingBet },
			)}
			data-focus={focusOddsKey === ticketKey}
			data-selected={selectAllOddsKey === ticketKey}
			data-disabled={!canBet}
			data-column={isParlay && !isMixParlay && (focusOddsKey === ticketKey || ticketStake > 0)}
			onClick={onFocus}
		>
			<div className="stake-input__stake">
				<div className="stake-input__textfield">
					{
						inputCurrency && SiteConfigsData?.EnableCurrencyDisplay
							? <div className="stake-input__currency">{inputCurrency}</div>
							: null
					}
					{
						ticketBetCount
							? <div className="stake-input__parlay-info">{`${ticketBetCount}X`}</div>
							: null
					}
					<div
						className="stake-input__input"
						data-placeholder={placeHolder}
					>
						{ticketStake ? addCommasWithFix(ticketStake) : ''}
					</div>
					<input ref={input}
						className="input"
						placeholder={placeHolder}
						type="input"
						value={ticketStake ? addCommasWithFix(ticketStake) : ''}
						onFocus={onFocus}
						onChange={onChange}
						onKeyDown={onKeyDown}
					/>
				</div>
			</div>
			<Tooltip ticketKey={ticketKey} />
		</div>
	);
};